import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './Donations.css';

interface DonationRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  street: string;
  town: string;
  province: string;
  postalCode: string;
  donationAmount: number;
  donationNote: string;
}

const Donations: React.FC = () => {
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof DonationRecord>('lastName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      const registrationsRef = collection(db, 'Registrations');
      const registrationsSnapshot = await getDocs(registrationsRef);
      
      console.log('Total registrations found:', registrationsSnapshot.docs.length);
      
      const donationRecords: DonationRecord[] = [];
      const recordsToProcess: Array<{
        registrationDoc: any;
        registrationData: any;
        donationAmount: number;
        donationNote: string;
      }> = [];

      // First pass: filter records with donations > $20
      for (const registrationDoc of registrationsSnapshot.docs) {
        const registrationData = registrationDoc.data();
        
        // Check for donation in different possible structures
        const donationAmount = registrationData.financial?.otherFees?.donations?.amount || 
                               registrationData.otherFees?.donations?.amount || 
                               0;
        const donationNote = registrationData.financial?.otherFees?.donations?.note || 
                             registrationData.otherFees?.donations?.note || 
                             '';
        
        console.log('Processing registration:', {
          id: registrationDoc.id,
          otherFees: registrationData.otherFees,
          donation: donationAmount,
          primaryCamperName: registrationData.registrationInfo?.primaryCamperName
        });
        
        console.log('Donation amount:', donationAmount);
        
        if (donationAmount <= 20) {
          console.log('Skipping - donation <= $20');
          continue;
        }

        recordsToProcess.push({
          registrationDoc,
          registrationData,
          donationAmount,
          donationNote
        });
      }

      console.log(`Processing ${recordsToProcess.length} records with donations > $20`);

      // Process primary campers from registrations
      const primaryCamperPromises = recordsToProcess.map(async (record) => {
        try {
          const primaryCamperId = record.registrationData.registrationInfo?.primaryCamperId;

          if (!primaryCamperId) {
            console.log('Skipping - no primaryCamperId in registration');
            return null;
          }
          
          const primaryCamperRef = doc(db, 'primaryCampers', primaryCamperId);
          const primaryCamperDoc = await getDoc(primaryCamperRef);
          
          if (!primaryCamperDoc.exists()) {
            console.log('Skipping - primary camper not found for:', primaryCamperId);
            return null;
          }
          
          const primaryCamperData = primaryCamperDoc.data();
          console.log('Found primary camper data:', primaryCamperData);
          console.log('Available fields in primaryCamperData:', Object.keys(primaryCamperData));

          // Get names from registration first, fallback to primaryCamper data
          const primaryCamperName = record.registrationData.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || primaryCamperData.FirstName || '';
          const lastName = nameParts.slice(1).join(' ') || primaryCamperData.LastName || '';

          return {
            id: record.registrationDoc.id,
            firstName,
            lastName,
            email: primaryCamperData.email || '',
            street: primaryCamperData.Street || '',
            town: primaryCamperData.Town || '',
            province: primaryCamperData.Province || '',
            postalCode: primaryCamperData.PostalCode || '',
            donationAmount: record.donationAmount,
            donationNote: record.donationNote
          };
        } catch (error) {
          console.error('Error fetching primary camper:', error);
          return null;
        }
      });

      const primaryCamperResults = await Promise.all(primaryCamperPromises);
      const finalRecords = primaryCamperResults.filter((record): record is DonationRecord => record !== null);

      console.log('Final donation records:', finalRecords);
      setDonations(finalRecords);
    } catch (err) {
      console.error('Error in fetchDonations:', err);
      setError('Error fetching donations: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof DonationRecord) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDonations = [...donations].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    return 0;
  });

  const totalDonations = donations.reduce((sum, record) => sum + record.donationAmount, 0);

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Street', 'Town', 'Province', 'Postal Code', 'Donation Amount', 'Note'];
    const csvContent = [
      headers.join(','),
      ...sortedDonations.map(record => [
        `"${record.firstName}"`,
        `"${record.lastName}"`,
        `"${record.email}"`,
        `"${record.street}"`,
        `"${record.town}"`,
        `"${record.province}"`,
        `"${record.postalCode}"`,
        record.donationAmount.toFixed(2),
        `"${(record.donationNote || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donations-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  if (loading) {
    return <div className="donations-loading">Loading donations...</div>;
  }

  if (error) {
    return <div className="donations-error">{error}</div>;
  }

  return (
    <div className="donations-report">
      <div className="donations-header">
        <h2>Donations Report</h2>
        <div className="donations-actions">
          <button onClick={printReport} className="print-button">
            Print Report
          </button>
          <button onClick={exportToCSV} className="export-button">
            Export to CSV
          </button>
        </div>
      </div>

      <div className="donations-summary">
        <p>Total Records: {donations.length}</p>
        <p>Total Donations: ${totalDonations.toFixed(2)}</p>
      </div>

      <div className="donations-table-container">
        <table className="donations-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('firstName')} className="sortable">
                First Name {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('lastName')} className="sortable">
                Last Name {sortField === 'lastName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('street')} className="sortable">
                Street {sortField === 'street' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('town')} className="sortable">
                Town {sortField === 'town' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('province')} className="sortable">
                Province {sortField === 'province' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('postalCode')} className="sortable">
                Postal Code {sortField === 'postalCode' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('donationAmount')} className="sortable">
                Donation Amount {sortField === 'donationAmount' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('donationNote')} className="sortable">
                Note {sortField === 'donationNote' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDonations.map((record) => (
              <tr key={record.id}>
                <td>{record.firstName}</td>
                <td>{record.lastName}</td>
                <td>{record.email}</td>
                <td>{record.street}</td>
                <td>{record.town}</td>
                <td>{record.province}</td>
                <td>{record.postalCode}</td>
                <td>${record.donationAmount.toFixed(2)}</td>
                <td style={{ fontStyle: record.donationNote ? 'italic' : 'normal', color: '#666' }}>
                  {record.donationNote || ''}
                </td>
              </tr>
            ))}
                     </tbody>
           <tfoot>
             <tr className="donations-total-row">
               <td colSpan={8} className="total-label">
                 <strong>Total Donations:</strong>
               </td>
               <td className="total-amount">
                 <strong>${totalDonations.toFixed(2)}</strong>
               </td>
             </tr>
           </tfoot>
         </table>
       </div>

      {donations.length === 0 && (
        <div className="no-donations">
          <p>No donations over $20 found.</p>
        </div>
      )}
    </div>
  );
};

export default Donations; 